import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/common/useAuth'
import { useSpaceRedirect } from '@/hooks/features/space/useSpaceRedirect'
import { PageLoading } from '@/components/common/layout/PageLoading'
import { Button } from '@/components/common/ui/button'
import { Card } from '@/components/common/ui/card'
import { logger } from '@/utils/logger'

export function SpaceRedirect() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { initialSpace, isLoading, authError, retryCount, manualRetry } = useSpaceRedirect()
  const [hasNavigated, setHasNavigated] = useState(false)

  useEffect(() => {
    if (!isLoading && !hasNavigated) {
      if (!user?.email) {
        logger.debug('ユーザー未認証、ログインページへリダイレクト')
        navigate('/login')
        setHasNavigated(true)
      } else if (initialSpace) {
        logger.debug('初期スペース発見、リダイレクト', { spaceId: initialSpace })
        navigate(`/space/${initialSpace}`)
        setHasNavigated(true)
      } else if (!authError) {
        logger.debug('スペースなし、ホームページへリダイレクト')
        navigate('/')
        setHasNavigated(true)
      }
    }
  }, [user, initialSpace, isLoading, authError, navigate, hasNavigated])

  const handleManualRetry = () => {
    logger.debug('手動リトライ実行', { retryCount })
    setHasNavigated(false) // ナビゲーション状態をリセット
    manualRetry()
  }

  const handleBackToLogin = () => {
    logger.debug('ログインページへ戻る')
    navigate('/login')
  }

  if (isLoading) {
    return <PageLoading text={t('common.loading.space')} />
  }

  if (authError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="p-8 max-w-md w-full mx-4 shadow-lg">
          <div className="text-center">
            <div className="mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-6 h-6 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-semibold mb-2 text-red-600">
                {t('auth.error.title', 'Authentication Error')}
              </h2>
              <p className="mb-4 text-gray-600">
                {t(
                  'auth.error.spaceAccess',
                  'Unable to access your spaces. Please try again or log in again.'
                )}
              </p>

              {/* 詳細エラー情報（開発時のみ表示） */}
              {import.meta.env.MODE === 'development' && (
                <div className="mb-4 p-3 bg-gray-100 rounded text-left">
                  <p className="text-xs text-gray-500 font-mono break-all">Error: {authError}</p>
                  <p className="text-xs text-gray-500 mt-1">Retry count: {retryCount}</p>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <Button
                onClick={handleManualRetry}
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    {t('common.retrying', 'Retrying...')}
                  </>
                ) : (
                  <>
                    {t('common.retry', 'Retry')}
                    {retryCount > 0 && ` (${retryCount + 1}回目)`}
                  </>
                )}
              </Button>

              <Button
                variant="outline"
                onClick={handleBackToLogin}
                className="w-full border-gray-300 hover:bg-gray-50"
              >
                {t('auth.backToLogin', 'Back to Login')}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    )
  }

  return null
}
